import { DbQueryService } from '@data';
import { BaseService } from '../../services/base.service';


// eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-explicit-any
type GConstructor<T = {}> = abstract new (...args: any[]) => T;

// properties that TrackChangesMixin needs
type QueryModel =  {
  baseService: BaseService;
  dbQueryService: DbQueryService;
  deactivate(): void;
  navigateBack(): void;
}

export function queryComponentMixin<TBase extends GConstructor<QueryModel>>(BaseClass: TBase) {

  abstract class QueryComponentMixin extends BaseClass {

    override deactivate() {
      super.deactivate();
      // -----------------------------------------------------------------
      // TODO (2026-05-20): clearRegisteredEntityTypes() is unsafe for any
      // QueryComponent that is NOT a route-level page — most importantly,
      // PrimeNG dialogs that extend QueryComponent.
      //
      // What this call does:
      //   It iterates dbQueryService.registeredEntityTypes and calls
      //   uow.clearEntities(type) on each, which calls setDetached() on
      //   every entity of those types currently in the singleton UoW.
      //
      // Why that breaks dialogs:
      //   A dialog tears down via PrimeNG's DynamicDialogRef.close() ->
      //   ngOnDestroy -> deactivate(). That destruction happens
      //   synchronously, in the same change-detection tick as the parent
      //   component's onClose subscription. If the parent's onClose
      //   handler adds, modifies, or otherwise depends on entities of any
      //   type in the dialog's registeredEntityTypes list, those entities
      //   get detached from the shared UoW the instant the dialog is
      //   destroyed — clobbering work the parent just did.
      //
      // The observable symptom is NG0100
      // (ExpressionChangedAfterItHasBeenChecked) on whatever the parent
      // binds to uow.hasChanges() — typically the Save button's
      // [disabled] in ProxSaveCancelComponent, which flips true -> false
      // mid-tick as the entities vanish. The hidden symptom is silent
      // data loss: the parent thinks it added entities but they were
      // detached before Save could persist them.
      //
      // Why this is only a latent bug everywhere else:
      //   Two conditions have to coincide to trip it: (a) a dialog
      //   extends QueryComponent AND (b) the dialog's dbQueryService
      //   registers an entity type that the parent simultaneously
      //   touches. Today only two dialogs in the codebase extend
      //   QueryComponent — KitDetailSelectDialogComponent (which trips
      //   on Kit/KitDetail) and InventoryApprovalSelectDialogComponent
      //   (latent — parent doesn't currently mutate the same types).
      //
      // The current workaround:
      //   KitDetailSelectDialogComponent overrides deactivate() to skip
      //   super.deactivate(). That's surgical but not principled.
      //
      // The right root fix:
      //   Make this clearing behavior route-scoped, not destruction-scoped.
      //   Options to consider when picking this up:
      //     1. Move clearRegisteredEntityTypes() out of deactivate() and
      //        into a route guard (e.g. canDeactivateGuard) so it only
      //        fires on actual route navigation, not on every component
      //        destruction.
      //     2. Add a `clearEntitiesOnDeactivate` flag (default true on
      //        QueryComponent, override to false in a DialogQueryComponent
      //        variant), so the policy is explicit rather than inherited
      //        by accident.
      //     3. Split QueryComponent into PageQueryComponent (clears on
      //        teardown) and DialogQueryComponent (does not). Cleaner but
      //        a bigger refactor with broader blast radius.
      //
      // Also: the older TODO "clear only at certain times, to better
      // preserve caching and avoid repeated queries" was the original
      // hint that this method's clearing policy is too eager. Same root
      // problem, different symptom.
      // -----------------------------------------------------------------
      this.dbQueryService.clearRegisteredEntityTypes();
    }

    async onCancel() {
      this.navigateBack();
    }

    showHelp(title?: string, field?: string): void {
      this.baseService.dialogService.helpDialog(title || '', this, field);
    }  

  }

  return QueryComponentMixin;
}
